import { useGlobalMutation } from 'state/query-client/hooks';
import { 
    upsertDatabaseConnection, 
    DatabaseConnectionRequest, 
    UpsertDatabaseConnectionResponse 
} from 'services/database-connection-service';
import { useConfig } from './use-config';

/**
 * Custom hook to create or update database connections
 *
 * This hook provides a mutation function to upsert database connections (PostgreSQL, MongoDB, MySQL).
 * It uses the global mutation pattern for consistent error handling and authentication management.
 *
 * Features:
 * - Automatic configuration injection
 * - Global error handling with authentication management
 * - Loading state management
 * - TypeScript support for request/response types
 *
 * @returns {Object} The mutation object with methods and state
 * @returns {Function} mutate - Function to trigger the database connection upsert
 * @returns {Function} mutateAsync - Async function to trigger the database connection upsert
 * @returns {boolean} isPending - Whether the mutation is in progress
 * @returns {boolean} isSuccess - Whether the mutation completed successfully
 * @returns {boolean} isError - Whether the mutation failed
 * @returns {UpsertDatabaseConnectionResponse | undefined} data - The upsert response data
 * @returns {Error | null} error - The error if mutation failed
 *
 * @example
 * const { mutate: upsertConnection, isPending } = useUpsertDatabaseConnection();
 * 
 * const handleSaveConnection = (formData) => {
 *   upsertConnection({
 *     databaseType: "postgres",
 *     scope: "user",
 *     host: "localhost",
 *     port: 5432,
 *     username: "postgres",
 *     password: "password",
 *     databaseName: "mydb"
 *   }, {
 *     onSuccess: (data) => {
 *       console.log('Connection saved:', data);
 *     },
 *     onError: (error) => {
 *       console.error('Failed to save connection:', error);
 *     }
 *   });
 * };
 */
export const useUpsertDatabaseConnection = () => {
    const { configurationSettings } = useConfig();

    return useGlobalMutation<UpsertDatabaseConnectionResponse, Error, DatabaseConnectionRequest>({
        mutationFn: async (payload: DatabaseConnectionRequest) => {
            if (!configurationSettings) {
                throw new Error('Configuration settings are not available');
            }
            return await upsertDatabaseConnection(configurationSettings, payload);
        },
    });
};

